import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiShield,
  FiTrash2,
  FiXCircle,
} from 'react-icons/fi';

import Button from '../../components/common/Button';
import configurationService from '../../services/api/configurationService';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;
const PageHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;
const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0;
  color: var(--ve-ink-800);
  font-size: 1.35rem;
`;
const Subtitle = styled.p`
  max-width: 760px;
  margin: 0.35rem 0 0;
  color: var(--ve-ink-500);
  line-height: 1.5;
`;
const Panel = styled.section`
  padding: 1.25rem;
  background: #fff;
  border: 1px solid var(--border-subtle);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
`;
const LegendGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.85rem;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
  }
`;
const LegendItem = styled.div`
  padding: 0.8rem;
  border: 1px solid var(--border-subtle);
  border-left: 4px solid ${({ $tone }) => (
    $tone === 'now' ? '#27834f' : $tone === 'restart' ? '#b47a00' : '#77529c'
  )};
  border-radius: 0.35rem;
  color: var(--ve-ink-700);
  font-size: 0.82rem;
  line-height: 1.45;

  strong { display: block; color: var(--ve-ink-800); margin-bottom: 0.25rem; }
`;
const CoverageCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.85rem;
  border: 1px solid var(--border-subtle);
  border-radius: 0.35rem;
  color: var(--ve-ink-700);
  font-size: 0.8rem;
  line-height: 1.45;

  h4 { margin: 0; color: var(--ve-ink-800); font-size: 0.9rem; }
  p { margin: 0; }
  code { color: var(--ve-ink-800); overflow-wrap: anywhere; }
`;
const CoverageLink = styled(RouterLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0.45rem 0.75rem;
  border: 1px solid var(--border-subtle);
  border-radius: 0.35rem;
  color: var(--ve-ink-700);
  background: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none;

  &:hover { border-color: var(--act-primary); color: var(--act-primary); }
  &:focus-visible { outline: 3px solid rgba(23, 98, 127, 0.25); }
`;
const ActivationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${({ $tone }) => (
    $tone === 'now' ? '#176b3a' : $tone === 'restart' ? '#725500' : '#573775'
  )};
  background: ${({ $tone }) => (
    $tone === 'now' ? '#eafaf1' : $tone === 'restart' ? '#fffbeb' : '#f3edf9'
  )};
`;
const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.8rem;
  border: 1px solid ${({ $error }) => ($error ? '#efb2ae' : '#bde5ca')};
  border-radius: 0.4rem;
  background: ${({ $error }) => ($error ? '#fff3f2' : '#f3fbf5')};
  color: ${({ $error }) => ($error ? '#8f231e' : '#176b3a')};
  font-size: 0.85rem;
  line-height: 1.45;

  svg { flex: 0 0 auto; margin-top: 0.12rem; }
`;
const Toolbar = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;
const SearchGroup = styled.div`
  flex: 1 1 340px;
  max-width: 620px;
`;
const Label = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  color: var(--ve-ink-800);
  font-size: 0.82rem;
  font-weight: 700;
`;
const SearchWrap = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 0.8rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--ve-ink-500);
    pointer-events: none;
  }
`;
const SearchInput = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 0.65rem 0.75rem 0.65rem 2.35rem;
  border: 1px solid var(--border-subtle);
  border-radius: 0.35rem;
  color: var(--ve-ink-800);
  background: #fff;

  &:focus-visible {
    outline: 3px solid rgba(23, 98, 127, 0.25);
    border-color: var(--act-primary);
  }
`;
const Count = styled.p`
  margin: 0.35rem 0 0;
  color: var(--ve-ink-500);
  font-size: 0.75rem;
`;
const Group = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 1.25rem;
  border: 1px solid var(--border-subtle);
  border-radius: 0.5rem;
  background: #fff;
`;
const GroupTitle = styled.legend`
  padding: 0 0.45rem;
  color: var(--ve-ink-800);
  font-size: 1rem;
  font-weight: 700;
`;
const SettingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;
const SettingCard = styled.article`
  min-width: 0;
  padding: 1rem;
  border: 1px solid ${({ $dirty, $error }) => (
    $error ? '#dc625c' : $dirty ? '#6aa5ba' : 'var(--border-subtle)'
  )};
  border-radius: 0.4rem;
  background: ${({ $dirty }) => ($dirty ? '#f7fbfc' : '#fff')};
`;
const SettingHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;
const SettingKey = styled.code`
  display: inline-block;
  color: var(--ve-ink-800);
  font-size: 0.82rem;
  font-weight: 700;
  overflow-wrap: anywhere;
`;
const Description = styled.p`
  margin: 0.45rem 0 0.8rem;
  color: var(--ve-ink-500);
  font-size: 0.8rem;
  line-height: 1.45;
`;
const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;
const SmallBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  background: ${({ $pending, $readonly, $warning }) => (
    $warning ? '#fff0ed' : $pending ? '#fff4d6' : $readonly ? '#f1eef5' : 'var(--surface-subtle)'
  )};
  color: ${({ $pending, $readonly, $warning }) => (
    $warning ? '#8f231e' : $pending ? '#725500' : $readonly ? '#573775' : 'var(--ve-ink-700)'
  )};
  font-size: 0.68rem;
  font-weight: 700;
`;
const StateGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
  margin: 0 0 0.85rem;

  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const StateItem = styled.div`
  min-width: 0;
  padding: 0.55rem;
  border-radius: 0.3rem;
  background: var(--surface-subtle);

  dt { color: var(--ve-ink-500); font-size: 0.68rem; }
  dd {
    margin: 0.18rem 0 0;
    color: var(--ve-ink-800);
    font-size: 0.78rem;
    font-weight: 700;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
`;
const Editor = styled.div`
  padding-top: 0.8rem;
  border-top: 1px solid var(--surface-subtle);
`;
const Input = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 0.65rem 0.75rem;
  border: 1px solid ${({ $error }) => ($error ? '#dc625c' : 'var(--border-subtle)')};
  border-radius: 0.35rem;
  color: var(--ve-ink-800);
  background: #fff;

  &:focus-visible {
    outline: 3px solid rgba(23, 98, 127, 0.25);
    border-color: var(--act-primary);
  }
`;
const Select = styled.select`
  width: 100%;
  min-height: 44px;
  padding: 0.65rem 0.75rem;
  border: 1px solid ${({ $error }) => ($error ? '#dc625c' : 'var(--border-subtle)')};
  border-radius: 0.35rem;
  color: var(--ve-ink-800);
  background: #fff;

  &:focus-visible {
    outline: 3px solid rgba(23, 98, 127, 0.25);
    border-color: var(--act-primary);
  }
`;
const Textarea = styled.textarea`
  width: 100%;
  min-height: 104px;
  padding: 0.65rem 0.75rem;
  resize: vertical;
  border: 1px solid ${({ $error }) => ($error ? '#dc625c' : 'var(--border-subtle)')};
  border-radius: 0.35rem;
  color: var(--ve-ink-800);
  background: #fff;

  &:focus-visible {
    outline: 3px solid rgba(23, 98, 127, 0.25);
    border-color: var(--act-primary);
  }
`;
const Hint = styled.p`
  margin: 0.4rem 0 0;
  color: var(--ve-ink-500);
  font-size: 0.72rem;
  line-height: 1.4;
`;
const FieldError = styled.p`
  margin: 0.4rem 0 0;
  color: var(--act-danger);
  font-size: 0.75rem;
  font-weight: 700;
`;
const SecretActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
  margin-top: 0.55rem;
`;
const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-height: 36px;
  padding: 0.35rem 0.55rem;
  border: 1px solid ${({ $danger }) => ($danger ? '#dc625c' : 'var(--border-subtle)')};
  border-radius: 0.3rem;
  background: #fff;
  color: ${({ $danger }) => ($danger ? 'var(--act-danger)' : 'var(--ve-ink-700)')};
  cursor: pointer;
  font-size: 0.72rem;
  font-weight: 700;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible { outline: 3px solid rgba(23, 98, 127, 0.25); }
`;
const ReadOnlyNote = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  margin: 0.75rem 0 0;
  color: var(--ve-ink-500);
  font-size: 0.75rem;
  line-height: 1.4;

  svg { flex: 0 0 auto; margin-top: 0.1rem; }
`;
const ActionBar = styled.div`
  position: sticky;
  bottom: 0.75rem;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.85rem 1rem;
  border: 1px solid var(--border-subtle);
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 4px 18px rgba(16, 41, 54, 0.14);
`;
const ActionText = styled.p`
  margin: 0;
  color: var(--ve-ink-700);
  font-size: 0.8rem;
  font-weight: 700;
`;
const ActionButtons = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
`;
const Empty = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: var(--ve-ink-500);
`;

const ACTIVATIONS = Object.freeze({
  effective_now: { tone: 'now', key: 'effectiveNow' },
  effective_after_restart: { tone: 'restart', key: 'afterRestart' },
  bootstrap_root_of_trust: { tone: 'root', key: 'rootOfTrust' },
});

const safeToken = (value) => String(value || 'unknown')
  .toLowerCase()
  .replace(/[^a-z0-9_]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 80) || 'unknown';

const humanize = (value) => String(value || 'other')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const hasOwn = (value, key) => Boolean(
  value && Object.prototype.hasOwnProperty.call(value, key)
);

const draftValueFor = (entry) => {
  if (entry.secret) return '';
  const hasStagedValue = entry.staged?.present && hasOwn(entry.staged, 'value');
  // An invalid deployed value is operational evidence, not a safe editing
  // baseline. Keep it hidden unless a separately validated staged override is
  // available for review.
  const raw = hasStagedValue
    ? entry.staged.value
    : entry.effective?.status === 'invalid'
      ? undefined
      : entry.effective?.value;
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'object') return JSON.stringify(raw, null, 2);
  return String(raw);
};

const displayValue = (value, t) => {
  if (value === null || value === undefined) return t('admin.configuration.notVisible');
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  if (String(value) === '') return t('admin.configuration.emptyValue');
  return String(value);
};

const configuredLabel = (configured, t) => {
  if (configured === true) return t('admin.configuration.configured');
  if (configured === false) return t('admin.configuration.notConfigured');
  return t('admin.configuration.notVisible');
};

const effectiveStatusLabel = (status, t) => {
  const keyByStatus = {
    known: 'known',
    derived: 'derived',
    invalid: 'invalid',
    unknown: 'unknown',
  };
  const key = keyByStatus[String(status || '').toLowerCase()];
  return key
    ? t(`admin.configuration.effectiveStatuses.${key}`)
    : t('admin.configuration.effectiveStatuses.notReported');
};

const optionValue = (option) => (
  option && typeof option === 'object' ? option.value : option
);

const optionLabel = (option) => (
  option && typeof option === 'object' ? (option.label || option.value) : option
);

const validationFor = (entry, raw, t) => {
  const rules = entry.validation && typeof entry.validation === 'object' ? entry.validation : {};
  const type = String(entry.type || 'string').toLowerCase();

  if (entry.secret && raw === '') return null;
  if (rules.required === true && String(raw).trim() === '') {
    return t('admin.configuration.validation.required');
  }
  if (rules.minLength !== undefined && String(raw).length < Number(rules.minLength)) {
    return t('admin.configuration.validation.minLength', { min: rules.minLength });
  }
  if (rules.maxLength !== undefined && String(raw).length > Number(rules.maxLength)) {
    return t('admin.configuration.validation.maxLength', { max: rules.maxLength });
  }
  if (type === 'boolean' && raw !== 'true' && raw !== 'false') {
    return t('admin.configuration.validation.boolean');
  }
  if (['integer', 'number'].includes(type)) {
    const number = Number(raw);
    if (raw === '' || !Number.isFinite(number) || (type === 'integer' && !Number.isInteger(number))) {
      return type === 'integer'
        ? t('admin.configuration.validation.integer')
        : t('admin.configuration.validation.number');
    }
    const min = rules.min ?? entry.min;
    const max = rules.max ?? entry.max;
    if (min !== undefined && number < Number(min)) {
      return t('admin.configuration.validation.min', { min });
    }
    if (max !== undefined && number > Number(max)) {
      return t('admin.configuration.validation.max', { max });
    }
  }
  if (['json', 'object', 'array'].includes(type)) {
    try {
      const parsed = JSON.parse(raw);
      if (type === 'array' && !Array.isArray(parsed)) return t('admin.configuration.validation.array');
      if (type === 'object' && (!parsed || Array.isArray(parsed) || typeof parsed !== 'object')) {
        return t('admin.configuration.validation.object');
      }
    } catch (_) {
      return t('admin.configuration.validation.json');
    }
  }
  if ((type === 'url' || ['http_url', 'origin'].includes(rules.format)) && raw) {
    try {
      const parsed = new URL(raw);
      if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password) {
        return t('admin.configuration.validation.url');
      }
      if (rules.format === 'origin' && parsed.origin !== raw.replace(/\/$/, '')) throw new Error('not an origin');
      if (rules.requireHttps === true && parsed.protocol !== 'https:') {
        return t('admin.configuration.validation.httpsRequired');
      }
      if (
        Array.isArray(rules.allowedHosts)
        && !rules.allowedHosts.some((host) => String(host).toLowerCase() === parsed.hostname.toLowerCase())
      ) {
        return t('admin.configuration.validation.approvedHost');
      }
      if (rules.allowNonStandardPort === false && parsed.port && parsed.port !== '443') {
        return t('admin.configuration.validation.standardHttpsPort');
      }
      if (rules.allowSearch === false && parsed.search) {
        return t('admin.configuration.validation.noUrlSearch');
      }
      if (rules.allowHash === false && parsed.hash) {
        return t('admin.configuration.validation.noUrlHash');
      }
    } catch (_) {
      return t('admin.configuration.validation.url');
    }
  }
  if (rules.format === 'origin_list' && raw) {
    const validOrigins = String(raw).split(',').map((item) => item.trim()).filter(Boolean).every((origin) => {
      try {
        const parsed = new URL(origin);
        return ['http:', 'https:'].includes(parsed.protocol) && parsed.origin === origin.replace(/\/$/, '');
      } catch (_) {
        return false;
      }
    });
    if (!validOrigins) return t('admin.configuration.validation.originList');
  }
  const options = entry.options || rules.options || rules.values;
  if (Array.isArray(options) && options.length && !options.some((option) => String(optionValue(option)) === String(raw))) {
    return t('admin.configuration.validation.option');
  }
  if (rules.pattern && raw) {
    try {
      if (!new RegExp(rules.pattern).test(raw)) return t('admin.configuration.validation.pattern');
    } catch (_) {
      // A malformed server-side display rule must not make the form unusable;
      // the authoritative backend validator still runs on save.
    }
  }
  return null;
};

const parsedValue = (entry, raw) => {
  const type = String(entry.type || 'string').toLowerCase();
  if (type === 'boolean') return raw === 'true';
  if (type === 'integer') return Number.parseInt(raw, 10);
  if (type === 'number') return Number(raw);
  if (['json', 'object', 'array'].includes(type)) return JSON.parse(raw);
  return raw;
};

const activationMeta = (activation) => ACTIVATIONS[activation] || { tone: 'root', key: 'unknown' };

const Configuration = () => {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState({ revision: null, settings: [], coverage: null });
  const [drafts, setDrafts] = useState({});
  const [baseline, setBaseline] = useState({});
  const [clearKeys, setClearKeys] = useState(() => new Set());
  const [fieldErrors, setFieldErrors] = useState({});
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const hydrate = useCallback((data) => {
    const settings = Array.isArray(data?.settings) ? data.settings : [];
    const values = {};
    settings.forEach((entry) => { values[entry.key] = draftValueFor(entry); });
    setCatalog({
      revision: data?.revision ?? null,
      settings,
      coverage: data?.coverage && typeof data.coverage === 'object' ? data.coverage : null,
    });
    setDrafts(values);
    setBaseline(values);
    setClearKeys(new Set());
    setFieldErrors({});
  }, []);

  const load = useCallback(async ({ preserveNotice = false } = {}) => {
    try {
      setLoading(true);
      setError(null);
      if (!preserveNotice) setNotice(null);
      const data = await configurationService.getConfiguration();
      hydrate(data);
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, [hydrate]);

  useEffect(() => { load(); }, [load]);

  const dirtyKeys = useMemo(() => new Set(
    catalog.settings
      .filter((entry) => entry.editable === true && (
        clearKeys.has(entry.key) || drafts[entry.key] !== baseline[entry.key]
      ))
      .map((entry) => entry.key)
  ), [baseline, catalog.settings, clearKeys, drafts]);

  useEffect(() => {
    if (!dirtyKeys.size) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirtyKeys.size]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return catalog.settings;
    return catalog.settings.filter((entry) => [
      entry.key,
      entry.category,
      entry.description,
      entry.scope,
      entry.activation,
      entry.runtimeStatus,
      entry.effective?.status,
      Array.isArray(entry.derivedFrom) ? entry.derivedFrom.join(' ') : entry.derivedFrom,
      entry.effective?.source,
    ].some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [catalog.settings, query]);

  const groups = useMemo(() => {
    const result = new Map();
    filtered.forEach((entry) => {
      const category = String(entry.category || 'other');
      if (!result.has(category)) result.set(category, []);
      result.get(category).push(entry);
    });
    return [...result.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, entries]) => ({
        category,
        entries: entries.slice().sort((a, b) => String(a.key).localeCompare(String(b.key))),
      }));
  }, [filtered]);

  const mappedError = useMemo(() => {
    if (!error) return null;
    const code = safeToken(error.code);
    const categories = {
      configuration_revision_conflict: 'revisionConflict',
      configuration_externally_managed: 'externallyManaged',
      configuration_invalid_value: 'invalidRequest',
      configuration_invalid_change: 'invalidRequest',
      configuration_invalid_request: 'invalidRequest',
      configuration_duplicate_key: 'invalidRequest',
      configuration_unknown_key: 'invalidRequest',
      configuration_revision_required: 'invalidRequest',
      validation_error: 'invalidRequest',
      configuration_secret_encryption_unavailable: 'secretEncryptionUnavailable',
      configuration_smtp_credential_pair_required: 'smtpCredentialPairRequired',
      configuration_state_unavailable: 'stateUnavailable',
      superadmin_required: 'superadminRequired',
    };
    const category = categories[code] || 'generic';
    const message = t(`admin.configuration.errors.${category}`);
    return error.requestId
      ? `${message} ${t('admin.configuration.errors.requestId', { requestId: error.requestId })}`
      : message;
  }, [error, t]);

  const updateDraft = (key, value) => {
    setDrafts((current) => ({ ...current, [key]: value }));
    setClearKeys((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
    setFieldErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
    setNotice(null);
  };

  const markOverrideForClear = (entry) => {
    const confirmationKey = entry.secret
      ? 'admin.configuration.confirmClearSecret'
      : 'admin.configuration.confirmClearOverride';
    if (!window.confirm(t(confirmationKey, { key: entry.key }))) return;
    setClearKeys((current) => new Set([...current, entry.key]));
    setDrafts((current) => ({ ...current, [entry.key]: '' }));
    setNotice(null);
  };

  const undoClear = (entry) => {
    setClearKeys((current) => {
      const next = new Set(current);
      next.delete(entry.key);
      return next;
    });
    // markOverrideForClear intentionally blanks the editor. Undo must restore
    // the exact pre-clear draft for ordinary values; secret drafts always stay
    // blank because Tempus never returns or prefills a credential.
    setDrafts((current) => ({
      ...current,
      [entry.key]: entry.secret ? '' : (baseline[entry.key] ?? ''),
    }));
    setFieldErrors((current) => {
      if (!current[entry.key]) return current;
      const next = { ...current };
      delete next[entry.key];
      return next;
    });
    setNotice(null);
  };

  const resetLocal = () => {
    setDrafts({ ...baseline });
    setClearKeys(new Set());
    setFieldErrors({});
    setNotice(null);
  };

  const refresh = () => {
    if (dirtyKeys.size && !window.confirm(t('admin.configuration.confirmDiscard'))) return;
    load();
  };

  const save = async (event) => {
    event.preventDefault();
    const errors = {};
    const changes = [];

    catalog.settings.forEach((entry) => {
      if (!dirtyKeys.has(entry.key)) return;
      if (clearKeys.has(entry.key)) {
        changes.push({ key: entry.key, clear: true });
        return;
      }
      const raw = drafts[entry.key] ?? '';
      const validationError = validationFor(entry, raw, t);
      if (validationError) {
        errors[entry.key] = validationError;
        return;
      }
      changes.push({ key: entry.key, value: parsedValue(entry, raw) });
    });

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      const first = catalog.settings.find((entry) => errors[entry.key]);
      if (first) document.getElementById(`configuration-${safeToken(first.key)}`)?.focus();
      return;
    }
    if (!changes.length) return;
    if (!window.confirm(t('admin.configuration.confirmSave', { count: changes.length }))) return;

    try {
      setSaving(true);
      setError(null);
      setNotice(null);
      const data = await configurationService.updateConfiguration({
        revision: catalog.revision,
        changes,
      });
      hydrate(data);
      setNotice(t('admin.configuration.saved', { count: changes.length }));
    } catch (requestError) {
      setError(requestError);
    } finally {
      setSaving(false);
    }
  };

  const renderEditor = (entry) => {
    const id = `configuration-${safeToken(entry.key)}`;
    const descriptionId = `${id}-help`;
    const errorId = `${id}-error`;
    const errorText = fieldErrors[entry.key];
    const describedBy = [descriptionId, errorText ? errorId : null].filter(Boolean).join(' ');
    const type = String(entry.type || 'string').toLowerCase();
    const rules = entry.validation && typeof entry.validation === 'object' ? entry.validation : {};
    const options = entry.options || rules.options || rules.values;
    const common = {
      id,
      name: entry.key,
      value: drafts[entry.key] ?? '',
      onChange: (event) => updateDraft(entry.key, event.target.value),
      'aria-describedby': describedBy,
      'aria-invalid': Boolean(errorText),
      $error: Boolean(errorText),
      disabled: clearKeys.has(entry.key),
    };

    if (entry.secret) {
      // Clear removes only the staged database override. An environment-only
      // credential cannot be removed by the running application.
      const canClear = entry.staged?.present === true;
      return (
        <Editor>
          <Label htmlFor={id}>{t('admin.configuration.replaceSecret')}</Label>
          <Input
            {...common}
            type="password"
            autoComplete="new-password"
            placeholder={t('admin.configuration.secretPlaceholder')}
            disabled={clearKeys.has(entry.key)}
          />
          <Hint id={descriptionId}>{t('admin.configuration.secretHint')}</Hint>
          {errorText && <FieldError id={errorId} role="alert">{errorText}</FieldError>}
          <SecretActions>
            {clearKeys.has(entry.key) ? (
              <>
                <SmallBadge $pending>{t('admin.configuration.clearOnSave')}</SmallBadge>
                <TextButton type="button" onClick={() => undoClear(entry)}>
                  {t('admin.configuration.undoClear')}
                </TextButton>
              </>
            ) : canClear ? (
              <TextButton
                type="button"
                $danger
                onClick={() => markOverrideForClear(entry)}
              >
                <FiTrash2 /> {t('admin.configuration.clearSecret')}
              </TextButton>
            ) : null}
          </SecretActions>
        </Editor>
      );
    }

    let control;
    if (type === 'boolean') {
      control = (
        <Select {...common}>
          <option value="true">{t('common.yes')}</option>
          <option value="false">{t('common.no')}</option>
        </Select>
      );
    } else if (Array.isArray(options) && options.length) {
      control = (
        <Select {...common}>
          {options.map((option) => (
            <option key={String(optionValue(option))} value={String(optionValue(option))}>
              {String(optionLabel(option))}
            </option>
          ))}
        </Select>
      );
    } else if (['json', 'object', 'array', 'multiline'].includes(type)) {
      control = <Textarea {...common} />;
    } else {
      control = (
        <Input
          {...common}
          type={['integer', 'number'].includes(type) ? 'number' : 'text'}
          inputMode={['integer', 'number'].includes(type) ? 'decimal' : undefined}
          step={type === 'integer' ? 1 : type === 'number' ? 'any' : undefined}
          min={rules.min ?? entry.min}
          max={rules.max ?? entry.max}
        />
      );
    }

    return (
      <Editor>
        <Label htmlFor={id}>{t('admin.configuration.newValue')}</Label>
        {control}
        <Hint id={descriptionId}>{t(`admin.configuration.typeHint.${[
          'integer', 'number', 'boolean', 'url', 'json', 'object', 'array',
        ].includes(type)
          ? type
          : ['http_url', 'origin'].includes(rules.format)
            ? 'url'
            : 'string'}`)}</Hint>
        {errorText && <FieldError id={errorId} role="alert">{errorText}</FieldError>}
        {entry.staged?.present === true && (
          <SecretActions>
            {clearKeys.has(entry.key) ? (
              <>
                <SmallBadge $pending>{t('admin.configuration.clearOnSave')}</SmallBadge>
                  <TextButton type="button" onClick={() => undoClear(entry)}>
                  {t('admin.configuration.undoClear')}
                </TextButton>
              </>
            ) : (
              <TextButton type="button" $danger onClick={() => markOverrideForClear(entry)}>
                <FiTrash2 /> {t('admin.configuration.clearOverride')}
              </TextButton>
            )}
          </SecretActions>
        )}
      </Editor>
    );
  };

  if (loading && !catalog.settings.length) {
    return (
      <Page>
        <PageHeader>
          <div>
            <Title><FiShield /> {t('admin.configuration.title')}</Title>
            <Subtitle>{t('admin.configuration.loading')}</Subtitle>
          </div>
        </PageHeader>
      </Page>
    );
  }

  if (!catalog.settings.length && error) {
    return (
      <Page>
        <PageHeader>
          <div>
            <Title><FiShield /> {t('admin.configuration.title')}</Title>
            <Subtitle>{t('admin.configuration.subtitle')}</Subtitle>
          </div>
        </PageHeader>
        <Notice $error role="alert"><FiXCircle /> {mappedError}</Notice>
        <div><Button type="button" onClick={() => load()}>{t('common.retry')}</Button></div>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <div>
          <Title><FiShield /> {t('admin.configuration.title')}</Title>
          <Subtitle>{t('admin.configuration.subtitle')}</Subtitle>
        </div>
        <Button type="button" variant="outline" onClick={refresh} disabled={saving || loading}>
          <FiRefreshCw /> {loading ? t('admin.configuration.refreshing') : t('admin.configuration.refresh')}
        </Button>
      </PageHeader>

      <Panel aria-labelledby="configuration-activation-title">
        <Title as="h3" id="configuration-activation-title"><FiClock /> {t('admin.configuration.activation.title')}</Title>
        <Subtitle>{t('admin.configuration.activation.subtitle')}</Subtitle>
        <LegendGrid>
          {['effective_now', 'effective_after_restart', 'bootstrap_root_of_trust'].map((activation) => {
            const meta = activationMeta(activation);
            return (
              <LegendItem key={activation} $tone={meta.tone}>
                <strong>{t(`admin.configuration.activation.${meta.key}.label`)}</strong>
                {t(`admin.configuration.activation.${meta.key}.description`)}
              </LegendItem>
            );
          })}
        </LegendGrid>
      </Panel>

      <Panel aria-labelledby="configuration-coverage-title">
        <Title as="h3" id="configuration-coverage-title">{t('admin.configuration.coverage.title')}</Title>
        <Subtitle>{t('admin.configuration.coverage.subtitle', {
          count: catalog.coverage?.environmentKeys ?? catalog.settings.length,
        })}</Subtitle>
        <LegendGrid>
          <CoverageCard>
            <h4>{t('admin.configuration.coverage.applicationTitle')}</h4>
            <p>{t('admin.configuration.coverage.applicationDescription')}</p>
            <code>{catalog.coverage?.runtimeApplicationSettings?.endpoint || '/api/settings'}</code>
            <CoverageLink to="/admin/settings">
              {t('admin.configuration.coverage.openSettings')}
            </CoverageLink>
          </CoverageCard>
          <CoverageCard>
            <h4>{t('admin.configuration.coverage.integrationsTitle')}</h4>
            <p>{t('admin.configuration.coverage.integrationsDescription')}</p>
            <code>{catalog.coverage?.integrationSettings?.endpoint || '/api/admin/integrations'}</code>
            <CoverageLink to="/admin/integrations">
              {t('admin.configuration.coverage.openIntegrations')}
            </CoverageLink>
          </CoverageCard>
        </LegendGrid>
      </Panel>

      {mappedError && <Notice $error role="alert"><FiXCircle /> {mappedError}</Notice>}
      {notice && <Notice role="status" aria-live="polite"><FiCheckCircle /> {notice}</Notice>}

      <Panel>
        <Toolbar>
          <SearchGroup>
            <Label htmlFor="configuration-search">{t('admin.configuration.searchLabel')}</Label>
            <SearchWrap>
              <FiSearch />
              <SearchInput
                id="configuration-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('admin.configuration.searchPlaceholder')}
              />
            </SearchWrap>
            <Count aria-live="polite">{t('admin.configuration.visibleCount', { shown: filtered.length, total: catalog.settings.length })}</Count>
          </SearchGroup>
          <Count>{t('admin.configuration.revision', { revision: catalog.revision || '—' })}</Count>
        </Toolbar>
      </Panel>

      <form onSubmit={save} noValidate>
        <Page>
          {groups.map(({ category, entries }) => (
            <Group key={category}>
              <GroupTitle>{humanize(category)}</GroupTitle>
              <SettingGrid>
                {entries.map((entry) => {
                  const meta = activationMeta(entry.activation);
                  const dirty = dirtyKeys.has(entry.key);
                  const effective = entry.effective || {};
                  const staged = entry.staged || {};
                  const effectiveInvalid = effective.status === 'invalid';
                  const effectiveUnknown = effective.status === 'unknown';
                  const effectiveText = effectiveInvalid
                    ? t('admin.configuration.invalidValueHidden')
                    : effectiveUnknown
                      ? t('admin.configuration.notVisible')
                      : entry.secret
                        ? configuredLabel(effective.configured, t)
                        : hasOwn(effective, 'value')
                          ? displayValue(effective.value, t)
                          : t('admin.configuration.notVisible');
                  const stagedText = entry.secret
                    ? configuredLabel(staged.configured, t)
                    : staged.present && hasOwn(staged, 'value')
                      ? displayValue(staged.value, t)
                      : t('admin.configuration.noStagedValue');

                  return (
                    <SettingCard
                      key={entry.key}
                      $dirty={dirty}
                      $error={Boolean(fieldErrors[entry.key]) || effectiveInvalid}
                      aria-labelledby={`configuration-${safeToken(entry.key)}-title`}
                    >
                      <SettingHead>
                        <SettingKey id={`configuration-${safeToken(entry.key)}-title`}>{entry.key}</SettingKey>
                        <BadgeRow>
                          <ActivationBadge $tone={meta.tone}>
                            {t(`admin.configuration.activation.${meta.key}.label`)}
                          </ActivationBadge>
                          {entry.pending && <SmallBadge $pending>{t('admin.configuration.pending')}</SmallBadge>}
                          {effectiveInvalid && (
                            <SmallBadge $warning>{t('admin.configuration.effectiveInvalid')}</SmallBadge>
                          )}
                          {effectiveUnknown && (
                            <SmallBadge $pending>{t('admin.configuration.effectiveUnknown')}</SmallBadge>
                          )}
                          {effective.securityStatus === 'insecure_development_default' && (
                            <SmallBadge $warning>{t('admin.configuration.insecureDevelopmentDefault')}</SmallBadge>
                          )}
                          {entry.runtimeStatus === 'unused' && <SmallBadge $readonly>{t('admin.configuration.runtimeUnused')}</SmallBadge>}
                          {entry.editable !== true && <SmallBadge $readonly>{t('admin.configuration.readOnly')}</SmallBadge>}
                          {dirty && <SmallBadge>{t('admin.configuration.unsaved')}</SmallBadge>}
                        </BadgeRow>
                      </SettingHead>
                      <Description>{entry.description || t('admin.configuration.noDescription')}</Description>
                      <StateGrid>
                        <StateItem>
                          <dt>{t('admin.configuration.effectiveValue')}</dt>
                          <dd>{effectiveText}</dd>
                        </StateItem>
                        <StateItem>
                          <dt>{t('admin.configuration.source')}</dt>
                          <dd>{effective.source ? humanize(effective.source) : t('admin.configuration.notVisible')}</dd>
                        </StateItem>
                        <StateItem>
                          <dt>{t('admin.configuration.effectiveStatus')}</dt>
                          <dd>{effectiveStatusLabel(effective.status, t)}</dd>
                        </StateItem>
                        <StateItem>
                          <dt>{t('admin.configuration.declaredDefault')}</dt>
                          <dd>{!entry.secret && hasOwn(entry, 'declaredDefault')
                            ? displayValue(entry.declaredDefault, t)
                            : entry.secret
                              ? t('admin.configuration.notVisible')
                              : t('admin.configuration.noDeclaredDefault')}</dd>
                        </StateItem>
                        <StateItem>
                          <dt>{t('admin.configuration.derivedFrom')}</dt>
                          <dd>{entry.derivedFrom
                            ? displayValue(entry.derivedFrom, t)
                            : '—'}</dd>
                        </StateItem>
                        <StateItem>
                          <dt>{t('admin.configuration.scope')}</dt>
                          <dd>{entry.scope ? humanize(entry.scope) : '—'}</dd>
                        </StateItem>
                        <StateItem>
                          <dt>{t('admin.configuration.stagedValue')}</dt>
                          <dd>{stagedText}</dd>
                        </StateItem>
                      </StateGrid>
                      {entry.runtimeStatus === 'unused' && (
                        <ReadOnlyNote><FiAlertTriangle /> {t('admin.configuration.runtimeUnusedHint')}</ReadOnlyNote>
                      )}
                      {effectiveInvalid && (
                        <ReadOnlyNote><FiAlertTriangle /> {t('admin.configuration.effectiveInvalidHint')}</ReadOnlyNote>
                      )}
                      {effectiveUnknown && (
                        <ReadOnlyNote><FiAlertTriangle /> {t('admin.configuration.effectiveUnknownHint')}</ReadOnlyNote>
                      )}
                      {effective.securityStatus === 'insecure_development_default' && (
                        <ReadOnlyNote><FiAlertTriangle /> {t('admin.configuration.insecureDevelopmentDefaultHint')}</ReadOnlyNote>
                      )}
                      {entry.editable === true ? renderEditor(entry) : (
                        <ReadOnlyNote>
                          <FiAlertTriangle />
                          {entry.activation === 'bootstrap_root_of_trust'
                            ? t('admin.configuration.rootReadOnly')
                            : t('admin.configuration.readOnlyHint')}
                        </ReadOnlyNote>
                      )}
                    </SettingCard>
                  );
                })}
              </SettingGrid>
            </Group>
          ))}
          {!groups.length && <Panel><Empty>{t('admin.configuration.noMatches')}</Empty></Panel>}

          <ActionBar aria-label={t('admin.configuration.actions')}>
            <ActionText>{dirtyKeys.size
              ? t('admin.configuration.unsavedCount', { count: dirtyKeys.size })
              : t('admin.configuration.noUnsaved')}</ActionText>
            <ActionButtons>
              <Button type="button" variant="outline" onClick={resetLocal} disabled={!dirtyKeys.size || saving}>
                {t('admin.configuration.resetLocal')}
              </Button>
              <Button type="submit" disabled={!dirtyKeys.size || saving}>
                {saving ? <FiRefreshCw /> : <FiSave />}
                {saving ? t('admin.configuration.saving') : t('admin.configuration.save')}
              </Button>
            </ActionButtons>
          </ActionBar>
        </Page>
      </form>
    </Page>
  );
};

export default Configuration;
